import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from './ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from './ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from './ui/tooltip';
import { Collapsible, CollapsibleContent } from './ui/collapsible';
import {
  Paperclip,
  Upload,
  Download,
  Trash2,
  Pencil,
  Eye,
  MoreVertical,
  FolderOpen,
  Image,
  FileText,
  Table2,
  Video,
  Music,
  Archive,
  Code,
  File,
  Search,
  Filter
} from 'lucide-react';
import FileUploadComponent from './FileUploadComponent';
import FilePreviewComponent from './FilePreviewComponent';
import fileService from '../services/fileService';

const FileAttachmentManager = ({
  entityType,
  entityId,
  title = '附件管理',
  maxFiles = 50,
  maxSizeMB = 50,
  allowedTypes = [],
  showUpload = true,
  showSearch = true,
  compact = false,
  files: controlledFiles,
  onChange,
}) => {
  // Determine if this is a controlled component
  const isControlled = controlledFiles !== undefined && onChange !== undefined;
  
  const [filesInternal, setFilesInternal] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showUploadArea, setShowUploadArea] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [previewFile, setPreviewFile] = useState(null);
  const [editDialog, setEditDialog] = useState({ open: false, file: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, file: null });

  // Get current files based on controlled/uncontrolled mode
  // eslint-disable-next-line no-unused-vars
  const _setFiles = isControlled ? (newFiles) => onChange(newFiles) : setFilesInternal;
  const files = isControlled ? controlledFiles : filesInternal;

  // Load files from server (only for uncontrolled mode)
  const loadFiles = useCallback(async () => {
    // Skip loading for controlled mode
    if (isControlled) return;
    
    try {
      setLoading(true);
      setError(null);
      if (!entityType || !entityId) {
        setFilesInternal([]);
        return;
      }
      const data = await fileService.getEntityFiles(entityType, entityId);
      setFilesInternal((data && data.attachments) ? data.attachments : []);
    } catch (err) {
      console.error('Failed to load files:', err);
      setError('加载文件失败');
      setFilesInternal([]);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId, isControlled]);

  // Load files on component mount and when entity changes (only for uncontrolled mode)
  useEffect(() => {
    if (!isControlled && entityType && entityId) {
      loadFiles();
    }
  }, [entityType, entityId, loadFiles, isControlled]);
  // Handle file upload completion
  const handleUploadComplete = useCallback((result, file) => {
    if (isControlled) {
      // For controlled mode, pass the new file to parent
      const newFile = {
        ...result,
        ...file,
        file_id: result.fileId || result.file_id,
        filename: result.filename || file.name,
        created_at: new Date().toISOString(),
      };
      onChange([...controlledFiles, newFile]);
    } else {
      // For uncontrolled mode, reload files from server
      loadFiles();
    }
  }, [isControlled, controlledFiles, onChange, loadFiles]);

  // Handle file upload error
  const handleUploadError = useCallback((error, file) => {
    console.error('Upload error:', error);
  }, []);

  // Download file
  const handleDownload = async (file) => {
    try {
      await fileService.downloadFile(file.file_id, file.filename);
    } catch (error) {
      console.error('Download error:', error);
      setError('下载文件失败');
    }
  };

  // Delete file
  const handleDelete = async (file) => {
    try {
      await fileService.deleteFile(file.file_id);
      setDeleteDialog({ open: false, file: null });
      loadFiles(); // Reload files
    } catch (error) {
      console.error('Delete error:', error);
      setError('删除文件失败');
    }
  };

  // Update file metadata
  const handleUpdateMetadata = async (file, updates) => {
    try {
      await fileService.updateFileMetadata(file.file_id, updates);
      setEditDialog({ open: false, file: null });
      loadFiles(); // Reload files
    } catch (error) {
      console.error('Update error:', error);
      setError('更新文件信息失败');
    }
  };

  // Get file icon
  const getFileIcon = (filename, category) => {
    const iconClass = 'h-6 w-6';

    switch (category || fileService.getFileCategory(filename)) {
      case 'image':
        return <Image className={`${iconClass} text-primary`} />;
      case 'document':
        return <FileText className={`${iconClass} text-destructive`} />;
      case 'spreadsheet':
        return <Table2 className={`${iconClass} text-success`} />;
      case 'video':
        return <Video className={`${iconClass} text-secondary-foreground`} />;
      case 'audio':
        return <Music className={`${iconClass} text-warning`} />;
      case 'archive':
        return <Archive className={`${iconClass} text-info`} />;
      case 'code':
        return <Code className={`${iconClass} text-primary`} />;
      default:
        return <File className={iconClass} />;
    }
  };

  // Filter files based on search and category
  const filteredFiles = files.filter(file => {
    const matchesSearch = !searchQuery ||
      file.filename.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (file.description && file.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (file.tags && file.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())));

    const matchesCategory = filterCategory === 'all' ||
      (file.category || fileService.getFileCategory(file.filename)) === filterCategory;

    return matchesSearch && matchesCategory;
  });

  // Get category counts
  const categoryCounts = files.reduce((acc, file) => {
    const category = file.category || fileService.getFileCategory(file.filename);
    acc[category] = (acc[category] || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="h-8 w-1/3 bg-muted/80 rounded animate-pulse mb-4" />
          <div className="h-48 w-full bg-muted/80 rounded animate-pulse" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Paperclip className="h-5 w-5" />
            <h3 className="text-lg font-semibold">{title}</h3>
            <Badge variant="secondary">{files.length}</Badge>
          </div>

          {showUpload && (
            <Button
              onClick={() => setShowUploadArea(!showUploadArea)}
              size="sm"
            >
              <Upload className="h-4 w-4 mr-2" />
              {showUploadArea ? '取消上传' : '上传文件'}
            </Button>
          )}
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription className="flex items-center justify-between">
              {error}
              <Button variant="ghost" size="sm" onClick={() => setError(null)}>
                关闭
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Upload Area */}
        {showUpload && (
          <Collapsible open={showUploadArea}>
            <CollapsibleContent>
              <div className="mb-4 p-4 bg-muted rounded-lg">
                <FileUploadComponent
                  entityType={entityType}
                  entityId={entityId}
                  onUploadComplete={handleUploadComplete}
                  onUploadError={handleUploadError}
                  maxFiles={maxFiles}
                  maxSizeMB={maxSizeMB}
                  allowedTypes={allowedTypes}
                />
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Search and Filter */}
        {showSearch && files.length > 0 && (
          <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="搜索文件名、描述或标签..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="pl-10">
                  <SelectValue placeholder="文件类型" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">所有类型 ({files.length})</SelectItem>
                  {Object.entries(categoryCounts).map(([category, count]) => (
                    <SelectItem key={category} value={category}>
                      {fileService.getCategoryDisplay(category)} ({count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* File List */}
        {files.length === 0 ? (
          <div className="text-center py-12">
            <FolderOpen className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h4 className="text-lg font-medium text-muted-foreground mb-2">暂无附件</h4>
            <p className="text-sm text-muted-foreground">
              {showUpload ? '点击上传按钮添加文件' : '还没有上传任何文件'}
            </p>
          </div>
        ) : filteredFiles.length === 0 ? (
          <div className="text-center py-12">
            <Search className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h4 className="text-lg font-medium text-muted-foreground mb-2">未找到匹配的文件</h4>
            <p className="text-sm text-muted-foreground">请尝试修改搜索条件</p>
          </div>
        ) : (
          <div className="divide-y">
            {filteredFiles.map((file, index) => (
              <div
                key={file.file_id}
                className="flex items-center p-3 hover:bg-muted cursor-pointer rounded-lg"
                onClick={() => setPreviewFile(file)}
              >
                <div className="mr-3">
                  {getFileIcon(file.filename, file.category)}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium truncate">{file.filename}</span>
                    <span className="text-xs text-muted-foreground">{file.displaySize}</span>
                  </div>
                  {file.description && (
                    <p className="text-xs text-muted-foreground truncate">{file.description}</p>
                  )}
                  {file.tags && file.tags.length > 0 && (
                    <div className="flex gap-1 mt-1">
                      {file.tags.slice(0, 3).map((tag, tagIndex) => (
                        <Badge key={tagIndex} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                      {file.tags.length > 3 && (
                        <Badge variant="secondary" className="text-xs">
                          +{file.tags.length - 3}
                        </Badge>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(file.created_at).toLocaleDateString()} • {file.categoryDisplay}
                  </p>
                </div>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setPreviewFile(file)}>
                            <Eye className="h-4 w-4 mr-2" />
                            预览
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDownload(file)}>
                            <Download className="h-4 w-4 mr-2" />
                            下载
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setEditDialog({ open: true, file })}>
                            <Pencil className="h-4 w-4 mr-2" />
                            编辑信息
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setDeleteDialog({ open: true, file })}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            删除
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TooltipTrigger>
                    <TooltipContent>更多操作</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* File Preview Dialog */}
      {previewFile && (
        <FilePreviewComponent
          file={previewFile}
          open={Boolean(previewFile)}
          onClose={() => setPreviewFile(null)}
          onDownload={() => handleDownload(previewFile)}
          onEdit={() => setEditDialog({ open: true, file: previewFile })}
          onDelete={() => setDeleteDialog({ open: true, file: previewFile })}
        />
      )}

      {/* Edit Dialog */}
      <EditFileDialog
        open={editDialog.open}
        file={editDialog.file}
        onClose={() => setEditDialog({ open: false, file: null })}
        onSave={handleUpdateMetadata}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteFileDialog
        open={deleteDialog.open}
        file={deleteDialog.file}
        onClose={() => setDeleteDialog({ open: false, file: null })}
        onConfirm={handleDelete}
      />
    </Card>
  );
};

// Edit File Dialog Component
const EditFileDialog = ({ open, file, onClose, onSave }) => {
  const [description, setDescription] = useState('');
  const [tagsString, setTagsString] = useState('');

  useEffect(() => {
    if (file) {
      setDescription(file.description || '');
      setTagsString(file.tags ? file.tags.join(', ') : '');
    }
  }, [file]);

  const handleSave = () => {
    const tags = tagsString.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
    onSave(file, { description, tags });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>编辑文件信息</DialogTitle>
        </DialogHeader>
        {file && (
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium">文件名: {file.filename}</p>
              <p className="text-xs text-muted-foreground">
                大小: {file.displaySize} • 类型: {file.categoryDisplay}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">描述</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="为此文件添加描述..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tags">标签</Label>
              <Input
                id="tags"
                value={tagsString}
                onChange={(e) => setTagsString(e.target.value)}
                placeholder="用逗号分隔多个标签"
              />
              <p className="text-xs text-muted-foreground">例如: 重要, 合同, 技术文档</p>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>取消</Button>
          <Button onClick={handleSave}>保存</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Delete File Dialog Component
const DeleteFileDialog = ({ open, file, onClose, onConfirm }) => {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>确认删除</DialogTitle>
        </DialogHeader>
        {file && (
          <p>
            确定要删除文件 <strong>{file.filename}</strong> 吗？此操作无法撤销。
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>取消</Button>
          <Button variant="destructive" onClick={() => onConfirm(file)}>
            删除
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FileAttachmentManager;
